"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useCallback } from "react";
import { Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  author: "Dr. Robert Miller",
  position: "Medical Director",
  company: "Metro Health Group",
  quote: "Ferio Solutions transformed our revenue cycle. Their precision in medical coding reduced our denial rate by 40% in just three months.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634544/img/avatar_jane_smith.png"
}, {
  id: 2,
  author: "Sarah Thompson",
  position: "Practice Manager",
  company: "Coastal Pediatrics",
  quote: "Their end-to-end administrative support allowed our staff to focus back on patient care. The credentialing process was remarkably fast.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634544/img/avatar_michael_lee.png"
}, {
  id: 3,
  author: "Dr. James Chen",
  position: "Founder",
  company: "OrthoCare Clinic",
  quote: "The transparency in their reporting and analytics gives us actionable insights we never had before. A true partner in our financial growth.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634544/img/avatar_sarah_jenkins.png"
}, {
  id: 4,
  author: "Elena Rodriguez",
  position: "CEO",
  company: "Wellness Diagnostics",
  quote: "Switching to Ferio was the best decision for our cash flow. Their expertise in US healthcare regulations ensures we are always compliant.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634544/img/avatar_david_chen.png"
}];
export default function VoicesOfAuthority() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const scrollTo = useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);
  return <section data-section-id="1290" id="provider-success-stories" className="bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="mb-16 md:mb-24 text-center max-w-3xl mx-auto">
          <span className="text-primary font-default font-semibold tracking-wider text-sm uppercase mb-4 block">
            Success Stories
          </span>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight mb-6">
            Trusted by Leading Healthcare Providers
          </h2>
          <p className="text-muted-foreground font-sans text-lg md:text-xl max-w-2xl mx-auto">
            See how Ferio Solutions optimizes revenue cycles and streamlines administrative burdens for medical practices nationwide.
          </p>
        </div>

        {}
        <div className="relative">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-6 pb-12">
              {testimonials.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-6 md:basis-1/2 lg:basis-1/2">
                  <Card className="h-full border border-border bg-card rounded-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary/10">
                    <CardContent className="p-8 md:p-10 flex flex-col h-full justify-between gap-8">
                      {}
                      <div className="space-y-6">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className="size-4 fill-chart-2 text-chart-2" />)}
                        </div>
                        <blockquote className="font-default text-2xl md:text-3xl leading-relaxed text-foreground italic">
                          &ldquo;{item.quote}&rdquo;
                        </blockquote>
                      </div>

                      {}
                      <div className="flex items-center gap-4 pt-4 border-t border-border">
                        <SafeHydrate><Avatar className="size-12 border-2 border-primary">
                          <AvatarImage src={item.avatar} alt={item.author} className="object-cover" />
                          <AvatarFallback className="bg-primary/10 text-primary font-default font-bold">
                            {item.author.split(" ").map(n => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <div className="font-default font-semibold text-foreground text-lg">
                            {item.author}
                          </div>
                          <div className="text-muted-foreground font-sans text-sm">
                            {item.position} at {item.company}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="flex justify-center gap-3 mt-8">
            {Array.from({
            length: count
          }).map((_, index) => <button key={index} onClick={() => scrollTo(index)} className={cn("h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring/50", current === index ? "w-8 bg-primary" : "w-2.5 bg-muted hover:bg-muted-foreground/20")} aria-label={`Go to slide ${index + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}